import type {
  ConditionOperator,
  StoryChoice,
  StoryCondition,
  StoryEffect,
  StoryProject,
  StoryState,
  StoryValue,
  StoryVariable,
  VariableType,
} from './types'

export const variableTypes: Array<{ value: VariableType; label: string }> = [
  { value: 'boolean', label: 'Yes / no' },
  { value: 'number', label: 'Number' },
  { value: 'text', label: 'Text' },
]

export const defaultValue = (type: VariableType): StoryValue =>
  type === 'boolean' ? false : type === 'number' ? 0 : ''

export function valueMatchesType(
  value: unknown,
  type: VariableType,
): value is StoryValue {
  return type === 'number'
    ? typeof value === 'number' && Number.isFinite(value)
    : typeof value === (type === 'boolean' ? 'boolean' : 'string')
}

export function operatorsFor(
  type: VariableType,
): Array<{ value: ConditionOperator; label: string }> {
  const equal: Array<{ value: ConditionOperator; label: string }> = [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
  ]
  if (type === 'number')
    return [
      ...equal,
      { value: 'gt', label: 'is greater than' },
      { value: 'gte', label: 'is at least' },
      { value: 'lt', label: 'is less than' },
      { value: 'lte', label: 'is at most' },
    ]
  return type === 'text'
    ? [...equal, { value: 'contains', label: 'contains' }]
    : equal
}

export const displayValue = (value: StoryValue) =>
  typeof value === 'boolean'
    ? value
      ? 'Yes'
      : 'No'
    : typeof value === 'string'
      ? `“${value}”`
      : String(value)

export function initialStoryState(project: StoryProject): StoryState {
  return Object.fromEntries(
    (project.variables ?? []).map((variable) => [
      variable.id,
      variable.initialValue,
    ]),
  )
}

export function conditionProblem(
  condition: StoryCondition,
  variables: StoryVariable[],
): string | null {
  const variable = variables.find((item) => item.id === condition.variableId)
  if (!variable) return 'A condition refers to a missing story variable.'
  if (
    !operatorsFor(variable.type).some(
      (operator) => operator.value === condition.operator,
    )
  )
    return `The condition for “${variable.name}” uses an incompatible comparison.`
  if (!valueMatchesType(condition.value, variable.type))
    return `The condition for “${variable.name}” needs a ${variable.type === 'boolean' ? 'yes / no' : variable.type} value.`
  return null
}

export function effectProblem(
  effect: StoryEffect,
  variables: StoryVariable[],
): string | null {
  const variable = variables.find((item) => item.id === effect.variableId)
  if (!variable) return 'A change refers to a missing story variable.'
  if (
    !['set', 'add', 'subtract'].includes(effect.operation) ||
    (effect.operation !== 'set' && variable.type !== 'number')
  )
    return `The change to “${variable.name}” uses an incompatible operation.`
  if (!valueMatchesType(effect.value, variable.type))
    return `The change to “${variable.name}” needs a ${variable.type === 'boolean' ? 'yes / no' : variable.type} value.`
  return null
}

export function testCondition(
  condition: StoryCondition,
  state: StoryState,
): boolean {
  const current = state[condition.variableId]
  const value = condition.value
  switch (condition.operator) {
    case 'eq':
      return current === value
    case 'neq':
      return current !== undefined && current !== value
    case 'gt':
      return (
        typeof current === 'number' &&
        typeof value === 'number' &&
        current > value
      )
    case 'gte':
      return (
        typeof current === 'number' &&
        typeof value === 'number' &&
        current >= value
      )
    case 'lt':
      return (
        typeof current === 'number' &&
        typeof value === 'number' &&
        current < value
      )
    case 'lte':
      return (
        typeof current === 'number' &&
        typeof value === 'number' &&
        current <= value
      )
    case 'contains':
      return (
        typeof current === 'string' &&
        typeof value === 'string' &&
        current.includes(value)
      )
    default:
      return false
  }
}

export function choiceAvailable(
  choice: StoryChoice,
  state: StoryState,
  variables: StoryVariable[],
): boolean {
  const conditions = choice.conditions ?? []
  if (!conditions.length) return true
  // Invalid rules never accidentally unlock a route, including an "any" group.
  if (conditions.some((condition) => conditionProblem(condition, variables)))
    return false
  if (
    conditions.some((condition) => {
      const variable = variables.find(
        (item) => item.id === condition.variableId,
      )!
      return !valueMatchesType(state[variable.id], variable.type)
    })
  )
    return false
  return choice.conditionMode === 'any'
    ? conditions.some((condition) => testCondition(condition, state))
    : conditions.every((condition) => testCondition(condition, state))
}

export function applyEffects(
  state: StoryState,
  effects: StoryEffect[],
  variables: StoryVariable[],
) {
  const errors = effects
    .map((effect) => effectProblem(effect, variables))
    .filter((error): error is string => Boolean(error))
  if (errors.length) return { state, errors }
  const next = { ...state }
  for (const effect of effects) {
    const variable = variables.find((item) => item.id === effect.variableId)!
    const current = next[variable.id]
    if (
      effect.operation !== 'set' &&
      !valueMatchesType(current, variable.type)
    ) {
      return {
        state,
        errors: [`“${variable.name}” does not have a valid current value.`],
      }
    }
    const value =
      effect.operation === 'set'
        ? effect.value
        : (current as number) +
          (effect.operation === 'add' ? 1 : -1) * (effect.value as number)
    if (!valueMatchesType(value, variable.type))
      return {
        state,
        errors: [
          `The change to “${variable.name}” is outside the supported number range.`,
        ],
      }
    next[variable.id] = value
  }
  return { state: next, errors: [] as string[] }
}

export function describeCondition(
  condition: StoryCondition,
  variables: StoryVariable[],
) {
  const variable = variables.find((item) => item.id === condition.variableId)
  const operator = operatorsFor(variable?.type ?? 'text').find(
    (item) => item.value === condition.operator,
  )
  return `${variable?.name || 'Missing variable'} ${operator?.label || condition.operator} ${displayValue(condition.value)}`
}

export function describeEffect(
  effect: StoryEffect,
  variables: StoryVariable[],
) {
  const name =
    variables.find((item) => item.id === effect.variableId)?.name ||
    'Missing variable'
  return effect.operation === 'set'
    ? `Set ${name} to ${displayValue(effect.value)}`
    : `${effect.operation === 'add' ? 'Add' : 'Subtract'} ${displayValue(effect.value)} ${effect.operation === 'add' ? 'to' : 'from'} ${name}`
}

export function variableUsage(project: StoryProject, variableId: string) {
  return project.nodes.filter(
    (node) =>
      node.effects?.some((effect) => effect.variableId === variableId) ||
      node.choices.some(
        (choice) =>
          choice.conditions?.some(
            (condition) => condition.variableId === variableId,
          ) ||
          choice.effects?.some((effect) => effect.variableId === variableId),
      ),
  )
}

export function storyLogicIssues(
  project: StoryProject,
): Array<{ nodeId: string; message: string }> {
  const variables = project.variables ?? []
  const issues: Array<{ nodeId: string; message: string }> = []
  for (const variable of variables) {
    if (!variable.name.trim())
      issues.push({
        nodeId: project.startNodeId,
        message: 'Give every story variable a name in Story bible.',
      })
    if (!valueMatchesType(variable.initialValue, variable.type))
      issues.push({
        nodeId: project.startNodeId,
        message: `“${variable.name}” has an invalid starting value.`,
      })
  }
  for (const node of project.nodes) {
    const messages = [
      ...(node.effects ?? []).map((effect) => effectProblem(effect, variables)),
      ...node.choices.flatMap((choice) => [
        ...(choice.conditions ?? []).map((condition) =>
          conditionProblem(condition, variables),
        ),
        ...(choice.effects ?? []).map((effect) =>
          effectProblem(effect, variables),
        ),
      ]),
    ]
    for (const message of new Set(messages.filter(Boolean)))
      issues.push({ nodeId: node.id, message: message! })
  }
  return issues
}
